import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type { RWTable, Table } from "common/data/table";
import uid from "common/utils/uid";
import type { EntityCopyCustomFunctionMap } from "./docEntityCustomCopyTypes";
import type { TableBasedAddDocEntityType } from "./tableBasedDocEntityHandle";

/**
 * Updates or duplicates entities in a table that share a common parent ID.
 */
export function docEntityTableUpdateChildren<T extends TableBasedAddDocEntityType>(
  entities: Table<DocEntityTypeToValueType[T]>,
  parentId: string,
  updatedFields: (entity: DocEntityTypeToValueType[T]) => Partial<DocEntityTypeToValueType[T]>,
  copyEntities: boolean,
  preserveOriginalMapping?: boolean,
) {
  return docEntityTableUpdateSome(
    entities,
    (e) => e.parentId === parentId,
    updatedFields,
    copyEntities,
    preserveOriginalMapping,
  );
}

/**
 * Updates or duplicates entities in a table by transferring them from an old parent to a new parent.
 * Optionally supports custom duplication logic for specific entity types.
 */
export function docEntityTableTransferParents<T extends TableBasedAddDocEntityType>(
  entities: Table<DocEntityTypeToValueType[T]>,
  oldParentId: string,
  newParentId: string,
  copyEntities: boolean,
  preserveOriginalMapping?: boolean,
  entityDuplicateCustomFunctions?: EntityCopyCustomFunctionMap,
) {
  const updatedFields = (entity: DocEntityTypeToValueType[T]) => {
    if (entityDuplicateCustomFunctions) {
      const customFunction = entityDuplicateCustomFunctions[entity.entityType];
      if (customFunction) {
        return customFunction(entity, newParentId);
      }
    }
    return { parentId: newParentId } as Partial<DocEntityTypeToValueType[T]>;
  };

  return docEntityTableUpdateChildren(entities, oldParentId, updatedFields, copyEntities, preserveOriginalMapping);
}

/**
 * Generic utility to update or duplicate entities in a table based on a predicate.
 * Returns the updated table and an optional mapping of old IDs to new IDs.
 */
export function docEntityTableUpdateSome<T extends TableBasedAddDocEntityType>(
  entities: Table<DocEntityTypeToValueType[T]>,
  predicate: (entity: DocEntityTypeToValueType[T]) => boolean,
  updatedFields: (entity: DocEntityTypeToValueType[T]) => Partial<DocEntityTypeToValueType[T]>,
  copyEntities: boolean,
  // map of the original entity id to the new entity id
  preserveOriginalMapping?: boolean,
) {
  const updates: RWTable<DocEntityTypeToValueType[T]> = {};
  const originalMapping: { [originalEntityId: string]: string /* newEntityId */ } = {};

  for (const id in entities) {
    const e: DocEntityTypeToValueType[T] = entities[id];

    if (!predicate(e)) {
      updates[id] = e;
      continue;
    }

    const newFields: Partial<DocEntityTypeToValueType[T]> = updatedFields(e);

    if (copyEntities) {
      updates[id] = e;
      const newId: string = uid();
      updates[newId] = { ...e, ...newFields, id: newId } as DocEntityTypeToValueType[T];

      if (preserveOriginalMapping) {
        originalMapping[id] = newId;
      }
    } else {
      updates[id] = { ...e, ...newFields } as DocEntityTypeToValueType[T];
    }
  }

  return {
    updatedEntities: updates as Table<DocEntityTypeToValueType[T]>,
    originalEntities: originalMapping,
  };
}
